import type { TechnicalConfigFieldKind } from "features/shared/lib/technical-config-tab/fieldKindStrategies";

import { FilterTypes } from "enums/components";
import { MONTHS } from "features/shared/lib/monthRanges";
import {
  type ActiveField,
  type CatalogColumnFieldOf,
  type CatalogHeaderFieldOf,
  type ConfigApiValueForKind,
  type DefinitionByKey as DefinitionByKeyFromFields,
  filterActiveFields,
  filterCatalogColumnFields,
  filterCatalogHeaderFields,
  filterTabFields,
  type FormStateFromFieldKeys,
  type FormValueForKind,
  isTabField,
  type TabFieldOf,
  type TechnicalConfigFieldDefinitionCore,
} from "features/shared/lib/technical-config-tab/fieldDefinitions";

import { COOLING_SYSTEMS, HEATING_SYSTEMS, T_AIR_SUPPLY_TYPES, VENTILATION_TYPES } from "./roomConfigOptions";

export const ROOM_CONFIG_TABS = {
  cooling: "cooling",
  general: "general",
  heating: "heating",
  schedules: "schedules",
  ventilation: "ventilation",
} as const;

export const ROOM_CONFIG_TAB_ORDER = [
  ROOM_CONFIG_TABS.general,
  ROOM_CONFIG_TABS.schedules,
  ROOM_CONFIG_TABS.heating,
  ROOM_CONFIG_TABS.cooling,
  ROOM_CONFIG_TABS.ventilation,
] as const;

export const ROOM_HEATING_SHARED_FIELD_KEYS = [
  "roomHeating",
  "usageStartCet",
  "usageEndCet",
  "usageSaturday",
  "usageSunday",
  "heatingSetPoint",
  "heatingSetPointOffHours",
  "heatingSetPointWarmUp",
] as const;

export const ROOM_CONFIG_DRAWER_HIDDEN_FIELD_KEYS = [
  // "airChangesPerHour",
  // "roomInfiltration",
  "tAirSupplyType",
] as const;

export type RoomConfigFieldDefinition = TechnicalConfigFieldDefinitionCore<RoomConfigFieldKind, RoomConfigTab> & {
  catalogColumn?: boolean | CatalogColumnFormat;
  catalogHeader?: boolean;
};

export type RoomConfigFieldKind = TechnicalConfigFieldKind;

type CatalogColumnFormat = "boolean" | "monthRanges" | "system" | "time";

type RoomConfigTab = (typeof ROOM_CONFIG_TABS)[keyof typeof ROOM_CONFIG_TABS];

const DEFAULT_COLOR = "#a629e0";
const ROOM_CONFIG_FIELD_DEFINITIONS = [
  {
    catalogColumn: true,
    catalogHeader: true,
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "string",
    key: "roomTypeName",
    kind: "string",
  },
  {
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    inputType: "string",
    isFieldInactive: true,
    key: "id",
    kind: "number",
  },
  {
    default: false,
    filterType: FilterTypes.SWITCH,
    inputType: "string",
    isFieldInactive: true,
    key: "isActive",
    kind: "boolean",
  },
  {
    catalogColumn: true,
    catalogHeader: true,
    default: DEFAULT_COLOR,
    filterType: FilterTypes.COLOR_INPUT,
    key: "color",
    kind: "string",
  },
  {
    catalogColumn: true,
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "string",
    isFieldInactive: true,
    key: "dinFunction",
    kind: "string",
  },
  {
    catalogColumn: true,
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "string",
    isFieldInactive: true,
    key: "group",
    kind: "string",
  },
  {
    catalogColumn: "boolean",
    default: true,
    filterType: FilterTypes.SWITCH,
    key: "roomHeating",
    kind: "boolean",
    tab: "general",
  },
  {
    catalogColumn: "boolean",
    default: true,
    filterType: FilterTypes.SWITCH,
    key: "roomCooling",
    kind: "boolean",
    tab: "general",
  },
  {
    catalogColumn: "boolean",
    default: false,
    filterType: FilterTypes.SWITCH,
    key: "usageSaturday",
    kind: "boolean",
    tab: "general",
  },
  {
    catalogColumn: "boolean",
    default: false,
    filterType: FilterTypes.SWITCH,
    key: "usageSunday",
    kind: "boolean",
    tab: "general",
  },
  {
    catalogColumn: "time",
    default: "08:00",
    filterType: FilterTypes.TIME_PICKER,
    key: "usageStartCet",
    kind: "time",
    section: "usage",
    tab: "general",
  },
  {
    catalogColumn: "time",
    default: "18:00",
    filterType: FilterTypes.TIME_PICKER,
    key: "usageEndCet",
    kind: "time",
    section: "usage",
    tab: "general",
  },
  {
    catalogColumn: true,
    default: "10",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "m2PerPerson",
    kind: "number",
    section: "occupancy",
    tab: "general",
  },
  {
    catalogColumn: true,
    default: "15",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "equipmentFixedLoadPerM2",
    kind: "number",
    section: "occupancy",
    tab: "general",
  },
  {
    catalogColumn: true,
    default: "12",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "illuminationPerM2",
    kind: "number",
    section: "occupancy",
    tab: "general",
  },
  {
    catalogColumn: "time",
    default: "06:00",
    filterType: FilterTypes.TIME_PICKER,
    key: "hvacStartCet",
    kind: "time",
    section: "hvac",
    tab: "heating",
  },
  {
    catalogColumn: "time",
    default: "22:00",
    filterType: FilterTypes.TIME_PICKER,
    key: "hvacEndCet",
    kind: "time",
    section: "hvac",
    tab: "heating",
  },
  {
    catalogColumn: "system",
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    key: "heatingSystem",
    kind: "enum",
    options: HEATING_SYSTEMS,
    tab: "heating",
  },
  {
    catalogColumn: "monthRanges",
    default: "",
    filterType: FilterTypes.MONTH_RANGES_MULTI,
    key: "heatingRanges",
    kind: "monthRanges",
    options: MONTHS,
    tab: "heating",
  },
  {
    catalogColumn: true,
    default: "21",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "heatingSetPoint",
    kind: "number",
    section: "setpoints",
    tab: "heating",
  },
  {
    catalogColumn: true,
    default: "20",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "heatingSetPointWarmUp",
    kind: "number",
    section: "setpoints",
    tab: "heating",
  },
  {
    catalogColumn: true,
    default: "16",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "heatingSetPointOffHours",
    kind: "number",
    section: "setpoints",
    tab: "heating",
  },
  {
    catalogColumn: true,
    default: "0.5",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "convectionFractionHeating",
    kind: "number",
    section: "setpoints",
    tab: "heating",
  },
  {
    catalogColumn: true,
    default: null,
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "heatingStart",
    kind: "number",
    section: "setpoints",
    tab: "heating",
  },
  {
    catalogColumn: true,
    default: null,
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "dynamicHeatingStartPointOffset",
    kind: "number",
    section: "setpoints",
    tab: "heating",
  },
  {
    catalogColumn: "system",
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    key: "coolingSystem",
    kind: "enum",
    options: COOLING_SYSTEMS,
    tab: "cooling",
  },
  {
    catalogColumn: "monthRanges",
    default: "",
    filterType: FilterTypes.MONTH_RANGES_MULTI,
    key: "coolingRanges",
    kind: "monthRanges",
    options: MONTHS,
    tab: "cooling",
  },
  {
    catalogColumn: true,
    default: "24",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "coolingSetPoint",
    kind: "number",
    section: "setpoints",
    tab: "cooling",
  },
  {
    catalogColumn: true,
    default: "26",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "coolingSetPointCoolDown",
    kind: "number",
    section: "setpoints",
    tab: "cooling",
  },
  {
    catalogColumn: true,
    default: "28",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "coolingSetPointExtreme",
    kind: "number",
    section: "setpoints",
    tab: "cooling",
  },
  {
    catalogColumn: true,
    default: "28",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "coolingSetPointOffHours",
    kind: "number",
    section: "setpoints",
    tab: "cooling",
  },
  {
    catalogColumn: true,
    default: null,
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "coolingStart",
    kind: "number",
    section: "setpoints",
    tab: "cooling",
  },
  {
    catalogColumn: true,
    default: null,
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "dynamicCoolingStartPointOffset",
    kind: "number",
    section: "setpoints",
    tab: "cooling",
  },
  {
    catalogColumn: true,
    default: "0.5",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "convectionFractionCooling",
    kind: "number",
    tab: "cooling",
  },
  {
    catalogColumn: "system",
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    key: "ventilationType",
    kind: "enum",
    options: VENTILATION_TYPES,
    tab: "ventilation",
  },
  {
    catalogColumn: true,
    default: "1.5",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "ventilationPerArea",
    kind: "number",
    tab: "ventilation",
  },
  {
    catalogColumn: true,
    default: "3",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    isFieldInactive: true,
    key: "airChangesPerHour",
    kind: "number",
    tab: "ventilation",
  },
  {
    catalogColumn: true,
    default: "0.5",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "excessAirChangesPerHour",
    kind: "number",
    tab: "ventilation",
  },
  {
    catalogColumn: true,
    default: null,
    filterType: FilterTypes.INPUT,
    inputType: "string",
    key: "roomInfiltration",
    kind: "number",
    tab: "ventilation",
  },
  {
    catalogColumn: true,
    default: "18",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "tAirSupply",
    kind: "number",
    tab: "ventilation",
  },
  {
    catalogColumn: true,
    default: "static",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    isFieldInactive: true,
    key: "tAirSupplyType",
    kind: "enum",
    options: T_AIR_SUPPLY_TYPES,
  },
] as const satisfies readonly RoomConfigFieldDefinition[];

export type ActiveFieldDefinition = ActiveField<(typeof ROOM_CONFIG_FIELD_DEFINITIONS)[number]>;

export type CatalogColumnFieldDefinition = CatalogColumnFieldOf<ActiveFieldDefinition> &
  Pick<RoomConfigFieldDefinition, "labelKey">;

export type CatalogHeaderFieldDefinition = CatalogHeaderFieldOf<ActiveFieldDefinition> &
  Pick<RoomConfigFieldDefinition, "colSpan" | "inputType" | "labelKey">;

export type DefinitionByKey<K extends RoomCatalogFormFieldKey> = DefinitionByKeyFromFields<
  typeof ROOM_CONFIG_FIELD_DEFINITIONS,
  K
>;

export type { FormValueForKind };

export type RoomCatalogFormFieldKey = ActiveFieldDefinition["key"];

export type RoomCatalogFormState = FormStateFromFieldKeys<
  typeof ROOM_CONFIG_FIELD_DEFINITIONS,
  RoomCatalogFormFieldKey
>;

// TODO: Fix: This hiddenly assumes that any field with catalogHeader: true is not a config form field.
export type RoomConfigFormFieldKey = Exclude<ActiveFieldDefinition, { catalogHeader: true }>["key"];

export type RoomConfigFormState = FormStateFromFieldKeys<typeof ROOM_CONFIG_FIELD_DEFINITIONS, RoomConfigFormFieldKey>;

export type RoomTypeConfigFields = {
  [K in RoomConfigFormFieldKey]: ConfigApiValueForKind<DefinitionByKey<K>["kind"]> | undefined;
};

export type TabFieldDefinition = Pick<
  RoomConfigFieldDefinition,
  "colSpan" | "inputType" | "labelKey" | "options" | "section"
> &
  TabFieldOf<ActiveFieldDefinition, RoomConfigTab>;

export { isTabField };

export const getActiveFieldDefinitions = (): readonly ActiveFieldDefinition[] =>
  filterActiveFields(ROOM_CONFIG_FIELD_DEFINITIONS);

export const getTabFieldDefinitions = (): readonly TabFieldDefinition[] => filterTabFields(getActiveFieldDefinitions());

export const getCatalogHeaderFieldDefinitions = (): readonly CatalogHeaderFieldDefinition[] =>
  filterCatalogHeaderFields(getActiveFieldDefinitions());

export const getCatalogColumnDefinitions = (): readonly CatalogColumnFieldDefinition[] =>
  filterCatalogColumnFields(getActiveFieldDefinitions());

export const getRoomConfigTabs = (): RoomConfigTab[] => [...ROOM_CONFIG_TAB_ORDER];
